import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { fmt } from '../helpers/helpers';
import moment from 'moment';

const CrossNoteValueChart = ({ entries }) => {
  // Check if we have multiple entries to show
  if (!entries || entries.length <= 1) {
    return null; // Don't show chart if only one entry
  }

  // Transform entries into chart data (chronological order - oldest first)
  const chartData = [...entries]
    .sort((a, b) => new Date(a.noteDate).getTime() - new Date(b.noteDate).getTime())
    .map(entry => ({
      noteDate: entry.noteDate,
      noteTitle: entry.noteTitle,
      date: moment(entry.noteDate).format('MMM/YY'),
      // Expenses are always negative; plot magnitude so "spending more" reads as the
      // line going up, not down. originalValue keeps the signed value for the tooltip.
      value: Math.abs(entry.value),
      originalValue: entry.value,
      isPaid: entry.is_paid
    }));

  // Zoom the Y-axis to the actual range so small variations are visible instead
  // of being squashed against a 0 baseline. Padding accounts for negative values.
  const values = chartData.map(d => d.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;
  const padding = range === 0 ? (Math.abs(maxValue) * 0.1 || 1) : range * 0.1;
  const yDomain = [minValue - padding, maxValue + padding];

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          background: 'white',
          border: '1px solid #ccc',
          padding: '8px 12px',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 'bold', color: '#333' }}>
            {data.noteTitle}
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: '#666' }}>
            {moment(data.noteDate).format('MMMM [de] YYYY')}
          </p>
          <p style={{ margin: '4px 0 0 0', fontWeight: 'bold', color: data.originalValue < 0 ? '#e74c3c' : '#27ae60' }}>
            {fmt(data.originalValue)}
          </p>
          {data.isPaid !== undefined && (
            <p style={{ margin: '4px 0 0 0', fontSize: '0.75rem', color: data.isPaid ? '#27ae60' : '#999' }}>
              {data.isPaid ? '✅ Pago' : '🕒 Pendente'}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Custom axis tick for dates
  const CustomAxisTick = ({ x, y, payload }) => {
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#666"
          fontSize="10"
          transform="rotate(-35)"
        >
          {payload.value}
        </text>
      </g>
    );
  };

  // Determine line color based on entry kind (all should be same kind)
  const lineColor = entries[0]?.value < 0 ? '#e74c3c' : '#27ae60';

  // Compact BRL for axis ticks (full amount is already shown in the tooltip)
  const fmtAxis = (value) => {
    const abs = Math.abs(value);
    if (abs >= 1000) return `R$ ${(value / 1000).toFixed(1).replace('.0', '')}k`;
    return `R$ ${value.toFixed(0)}`;
  };

  return (
    <div className="cross-note-chart">
      <h4>📊 Valores entre Notas</h4>
      <ResponsiveContainer width="100%" height={220}>
        <LineChart
          data={chartData}
          margin={{
            top: 10,
            right: 10,
            left: 10,
            bottom: 50,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            height={50}
            tick={CustomAxisTick}
          />
          <YAxis
            width={80}
            domain={yDomain}
            tickFormatter={fmtAxis}
          />
          <Tooltip content={CustomTooltip} />
          <Line
            type="monotone"
            dataKey="value"
            stroke={lineColor}
            strokeWidth={2}
            dot={{ fill: lineColor, r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default CrossNoteValueChart;
